/**
 * Task seed data - Consistent pre-configured tasks based on Axioma Project Process
 */

import { faker } from '@faker-js/faker'
import { Task, Project, Status, TaskType, Priority, Horizon } from '@/types'
import { PROJECT_TEMPLATES } from './projects.seed'

interface TaskTemplate {
  name: string
  description: string
  type: TaskType
  status: Status
  priority: Priority
  horizon: Horizon
  hasDueDate: boolean
}

/**
 * Task template arrays organized by index (matches PROJECT_TEMPLATES order)
 */
const TASK_TEMPLATE_ARRAYS: TaskTemplate[][] = [
  // Index 0: Client Requirements & Contract Setup
  [
    {
      name: 'Identify key stakeholders',
      description:
        'Identify stakeholders, their responsibilities, involvement extent, and approval authority',
      type: TaskType.Task,
      status: Status.Done,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Contact existing website team',
      description: 'Get in touch with anyone who has context for the existing website and tooling',
      type: TaskType.Task,
      status: Status.Done,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Schedule requirements meeting',
      description: 'Schedule initial requirements gathering meeting with stakeholders',
      type: TaskType.Milestone,
      status: Status.Done,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Outline project scope',
      description:
        'Outline entire scope of work based on current knowledge to create actionable tasks',
      type: TaskType.Task,
      status: Status.Done,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Determine project timeline',
      description: 'Work with stakeholders to establish realistic project timeline and milestones',
      type: TaskType.Task,
      status: Status.Done,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Create client contract',
      description:
        'Use answers to questions to create contract for client. Read and revise multiple times',
      type: TaskType.Task,
      status: Status.Done,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Send W-9 to client',
      description: 'Send copy of W-9 to client after contract is signed',
      type: TaskType.Approval,
      status: Status.Done,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
  ],
  // Index 1: Website Design Implementation
  [
    {
      name: 'Set up breakpoints',
      description: 'Configure responsive breakpoints per style guide specifications',
      type: TaskType.Task,
      status: Status.Done,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Set up typography scale',
      description: 'Configure typography scale variables/tokens across breakpoints per style guide',
      type: TaskType.Task,
      status: Status.Done,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Set up layout specifications',
      description: 'Configure layout specifications across breakpoints per style guide',
      type: TaskType.Task,
      status: Status.InProgress,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Set up color palette',
      description: 'Configure color palette variables/tokens per style guide',
      type: TaskType.Task,
      status: Status.InProgress,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Set up spacing tokens',
      description: 'Configure spacing variables/tokens per style guide',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement global typography',
      description: 'Implement design for global typography elements per style guide',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement global links',
      description: 'Implement design for global link elements per style guide',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement global buttons',
      description: 'Implement design for global button elements per style guide',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement global header',
      description: 'Implement design for global header component per style guide',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement global footer',
      description: 'Implement design for global footer component per style guide',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement global layout',
      description: 'Implement design for global layout structure per style guide',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement page designs',
      description: 'Implement all page-specific designs across the website',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
    {
      name: 'Test designs across breakpoints',
      description: 'Test implemented page designs across each responsive breakpoint',
      type: TaskType.Milestone,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
  ],
  // Index 2: Website Accessibility Audit
  [
    {
      name: 'Check color contrast',
      description: 'Verify color contrast meets WCAG 2.1 standards using WebAIM Contrast Checker',
      type: TaskType.Task,
      status: Status.Done,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement semantic HTML',
      description: 'Ensure semantic HTML elements are used across all pages',
      type: TaskType.Task,
      status: Status.InProgress,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Run Wave accessibility evaluation',
      description:
        'Run WebAIM Wave accessibility evaluation tool across all pages and make improvements',
      type: TaskType.Task,
      status: Status.InProgress,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Add image alt text',
      description: 'Ensure all images have descriptive alt text',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Verify tab order',
      description: 'Ensure tab order and keyboard navigation are logical throughout the site',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement hover states',
      description:
        'Ensure hover and active states are clear and consistent for interactive elements',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Verify typography readability',
      description: 'Ensure typography is readable across all contexts',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Test keyboard navigation',
      description: 'Verify keyboard navigation works across entire site including dropdowns',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement ARIA roles',
      description: 'Add ARIA roles and labels where needed for assistive technology',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Add form labels',
      description: 'Ensure all form inputs have proper labels',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test screen readers',
      description: 'Verify screen readers can navigate and understand the site',
      type: TaskType.Milestone,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
  ],
  // Index 3: Web Performance Optimization
  [
    {
      name: 'Run Lighthouse tests',
      description: 'Run desktop and mobile Lighthouse tests across all pages in Chromium browser',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Optimize page load times',
      description: 'Ensure all pages load under 3 seconds',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Optimize images',
      description: 'Serve images in modern formats (webp, avif) with lazy loading',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Optimize videos',
      description: 'Serve videos in modern formats (webm, mp4 with H.264 compression)',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Minify CSS and JavaScript',
      description: 'Ensure all CSS and JavaScript are minified for production',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Remove unused code',
      description: 'Eliminate unused CSS and JavaScript from the codebase',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Inline critical CSS/JS',
      description: 'Inline CSS and JavaScript as necessary for critical rendering path',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Defer third-party scripts',
      description: 'Configure third-party script loading to be deferred',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Add loading indicators',
      description: 'Implement loading indicators or skeleton screens where needed',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Low,
      horizon: Horizon.Next,
      hasDueDate: false,
    },
  ],
  // Index 4: Cross-Browser Testing
  [
    {
      name: 'Test on Google Chrome',
      description: 'Test website functionality and layout on Google Chrome',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test on Safari',
      description: 'Test website functionality and layout on Safari',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test on Microsoft Edge',
      description: 'Test website functionality and layout on Microsoft Edge',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test on Firefox',
      description: 'Test website functionality and layout on Firefox',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test on mobile device',
      description: 'Test website on real-world mobile device for touch interactions and viewport',
      type: TaskType.Milestone,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
  ],
  // Index 5: Developer Experience & Documentation
  [
    {
      name: 'Document architecture',
      description: 'Write comprehensive documentation on project architecture',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
    {
      name: 'Document tooling',
      description: 'Write documentation for development tooling and build processes',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
    {
      name: 'Consider maintainer needs',
      description: 'Review and document considerations for future website maintainers',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Low,
      horizon: Horizon.Next,
      hasDueDate: false,
    },
  ],
  // Index 6: User Experience Enhancement
  [
    {
      name: 'Verify type sizes',
      description: 'Ensure type sizes are readable across all devices',
      type: TaskType.Task,
      status: Status.InProgress,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Create useful 404 pages',
      description: 'Design and implement helpful 404 error pages',
      type: TaskType.Task,
      status: Status.InProgress,
      priority: Priority.Low,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Add loading indicators',
      description: 'Implement loading indicators or skeleton screens where needed',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Add password visibility toggle',
      description: 'Implement content visibility toggle for password fields',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test button actions',
      description: 'Verify all buttons trigger correct actions',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement feedback messages',
      description: 'Add error and success message displays throughout the application',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test browser navigation',
      description: 'Verify back/forward browser buttons function properly',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test mobile navigation',
      description: 'Verify mobile navigation opens and closes correctly',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Validate form fields',
      description: 'Ensure form fields validate correctly (required, format, length)',
      type: TaskType.Milestone,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
  ],
  // Index 7: Design & Visual Consistency Review
  [
    {
      name: 'Verify layout consistency',
      description: 'Ensure consistent layout, fonts, and spacing throughout the site',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Review visual hierarchy',
      description: 'Verify visual hierarchy and information architecture are clear',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Check image resolution',
      description: 'Ensure all images render at proper resolution',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Verify color consistency',
      description: 'Ensure consistent color usage across the site',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Verify iconography consistency',
      description: 'Ensure consistent iconography style and usage',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
    {
      name: 'Verify border consistency',
      description: 'Ensure consistent borders and border radii across components',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Low,
      horizon: Horizon.Now,
      hasDueDate: false,
    },
  ],
  // Index 8: SEO Implementation
  [
    {
      name: 'Add social share images',
      description: 'Add OpenGraph, LinkedIn, and Twitter social share images for each page',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Add favicon',
      description: 'Create and implement favicon for the website',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Add page titles',
      description: 'Ensure each page has unique title between 50-70 characters',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Add meta descriptions',
      description: 'Ensure each page has unique meta description less than 155 characters',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Structure header tags',
      description: 'Verify header tags follow hierarchical and logical structure',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Add image alt text',
      description: 'Ensure all images have descriptive alt text for SEO',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Implement canonical tags',
      description: 'Add canonical tags to prevent duplicate content issues',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Create sitemap',
      description: 'Generate and implement XML sitemap',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Configure robots.txt',
      description: 'Create and configure robots.txt file',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Verify page load speeds',
      description: 'Ensure all pages load under 3 seconds for SEO',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test all links',
      description: 'Verify all internal and external links work correctly',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Set up redirects',
      description: 'Configure necessary URL redirects',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
  ],
  // Index 9: Copywriting & Content Review
  [
    {
      name: 'Spell-check content',
      description: 'Run spell-check across all website content',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Grammar-proof content',
      description: 'Review and correct grammar across all website content',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Optimize landing page headlines',
      description: 'Ensure headlines describe solutions to visitor problems',
      type: TaskType.Approval,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
  ],
  // Index 10: Calls to Action Optimization
  [
    {
      name: 'Determine primary CTA',
      description: 'Define primary call to action (e.g., purchase, sign up)',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: false,
    },
    {
      name: 'Implement primary CTA',
      description: 'Place prominent primary CTA in header and hero sections',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: false,
    },
    {
      name: 'Determine secondary CTAs',
      description: 'Define 1-2 secondary calls to action (e.g., learn more, case studies)',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.Medium,
      horizon: Horizon.Next,
      hasDueDate: false,
    },
    {
      name: 'Implement secondary CTAs',
      description: 'Place secondary CTAs throughout landing page content',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.Medium,
      horizon: Horizon.Next,
      hasDueDate: false,
    },
    {
      name: 'Determine tertiary CTAs',
      description: 'Define 1-5 tertiary calls to action (e.g., newsletter, social follow)',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.Low,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
    {
      name: 'Implement tertiary CTAs',
      description: 'Place tertiary CTAs in header and footer areas',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.Low,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
  ],
  // Index 11: Marketing Analytics Setup
  [
    {
      name: 'Install analytics software',
      description: 'Install and configure Google Analytics or alternative tracking solution',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
    {
      name: 'Verify tracking',
      description: 'Test and verify analytics tracking is working correctly',
      type: TaskType.Milestone,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
  ],
  // Index 12: DevOps & Monitoring
  [
    {
      name: 'Schedule automated backups',
      description: 'Configure and test automated backup schedule',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
    {
      name: 'Enable monitoring tools',
      description: 'Set up and configure website monitoring and alerting tools',
      type: TaskType.Milestone,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
  ],
  // Index 13: Security Implementation
  [
    {
      name: 'Enforce HTTPS',
      description: 'Configure SSL/TLS certificates and enforce HTTPS across entire site',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Fix mixed-content warnings',
      description: 'Identify and resolve any mixed-content warnings',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Protect input fields',
      description: 'Implement XSS and SQL injection protection for all input fields',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Secure password fields',
      description: 'Ensure password fields are properly masked and secured',
      type: TaskType.Milestone,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
  ],
  // Index 14: Legal Compliance Setup
  [
    {
      name: 'Implement cookie consent',
      description: 'Add cookie consent banners that meet GDPR and CCPA requirements',
      type: TaskType.Approval,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Next,
      hasDueDate: true,
    },
  ],
  // Index 15: Website Launch
  [
    {
      name: 'Get domain access',
      description: 'Obtain domain name access credentials from stakeholders',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
    {
      name: 'Draft launch plan',
      description: 'Create detailed launch plan and share with stakeholders for approval',
      type: TaskType.Approval,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
    {
      name: 'Schedule launch',
      description: 'Mark launch date and time on calendar',
      type: TaskType.Milestone,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Someday,
      hasDueDate: true,
    },
    {
      name: 'Execute launch',
      description: 'Launch the website according to the approved plan',
      type: TaskType.Milestone,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Someday,
      hasDueDate: true,
    },
    {
      name: 'Monitor launched website',
      description: 'Monitor website performance and errors post-launch',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Someday,
      hasDueDate: true,
    },
  ],
  // Index 16: Component Quality Assurance
  [
    {
      name: 'Test menu links',
      description: 'Verify all navigation and menu links work correctly',
      type: TaskType.Task,
      status: Status.InProgress,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test browser navigation',
      description: 'Verify back/forward browser buttons function properly',
      type: TaskType.Task,
      status: Status.InProgress,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test mobile navigation',
      description: 'Verify mobile navigation opens and closes correctly',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Validate form fields',
      description: 'Test form field validation (required, format, length)',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test form messages',
      description: 'Verify error and success messages display correctly',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test form tab order',
      description: 'Verify tab order and keyboard navigation in forms',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test form submissions',
      description: 'Verify form submissions route correctly',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test form buttons',
      description: 'Verify reset and submit buttons function as expected',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test form labels',
      description: 'Ensure all form inputs have proper labels',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test button actions',
      description: 'Verify all buttons trigger correct actions',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test button states',
      description: 'Verify hover and active states are clear for all buttons',
      type: TaskType.Task,
      status: Status.ToDo,
      priority: Priority.Medium,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
    {
      name: 'Test dropdown keyboard navigation',
      description: 'Verify dropdown options can be navigated using keyboard',
      type: TaskType.Milestone,
      status: Status.ToDo,
      priority: Priority.High,
      horizon: Horizon.Now,
      hasDueDate: true,
    },
  ],
  // Index 17: Project Handoff & Documentation
  [
    {
      name: 'Write project documentation',
      description: 'Create comprehensive documentation for stakeholders',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
    {
      name: 'Write README documentation',
      description: 'Create README documentation for web development tooling and systems',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
    {
      name: 'Hand off to stakeholders',
      description: 'Conduct project handoff meeting with stakeholders',
      type: TaskType.Milestone,
      status: Status.Draft,
      priority: Priority.High,
      horizon: Horizon.Someday,
      hasDueDate: true,
    },
    {
      name: 'Project reflection',
      description: 'Reflect on project outcomes and lessons learned',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.Low,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
    {
      name: 'Create portfolio shots',
      description: 'Create follow-up tasks to create shots for Dribbble',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.Low,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
    {
      name: 'Create case study',
      description: 'Create follow-up tasks to write project case study',
      type: TaskType.Task,
      status: Status.Draft,
      priority: Priority.Low,
      horizon: Horizon.Someday,
      hasDueDate: false,
    },
  ],
]

/**
 * Build mapping of project names to task templates using imported PROJECT_TEMPLATES
 */
const TASK_TEMPLATES_BY_PROJECT: Record<string, TaskTemplate[]> = PROJECT_TEMPLATES.reduce(
  (acc, projectTemplate, index) => {
    acc[projectTemplate.name] = TASK_TEMPLATE_ARRAYS[index] || []
    return acc
  },
  {} as Record<string, TaskTemplate[]>
)

/**
 * Define logical dependencies between tasks (by template index)
 * Format: [dependentTaskIndex]: [prerequisiteTaskIndex1, prerequisiteTaskIndex2, ...]
 */
const TASK_DEPENDENCIES_BY_PROJECT_INDEX: Record<number, number[]>[] = [
  // Index 0: Client Requirements & Contract Setup
  {
    2: [0, 1], // Schedule meeting depends on identifying stakeholders and contacting team
    3: [2], // Outline scope depends on requirements meeting
    4: [3], // Determine timeline depends on scope
    5: [3, 4], // Create contract depends on scope and timeline
    6: [5], // Send W-9 depends on contract
  },
  // Index 1: Website Design Implementation
  {
    5: [0, 1, 2, 3, 4], // Typography implementation depends on all setup tasks
    6: [0, 1, 2, 3, 4], // Links depend on all setup tasks
    7: [0, 1, 2, 3, 4], // Buttons depend on all setup tasks
    8: [5, 6, 7], // Header depends on typography, links, buttons
    9: [5, 6, 7], // Footer depends on typography, links, buttons
    10: [8, 9], // Layout depends on header and footer
    11: [10], // Page designs depend on layout
    12: [11], // Testing depends on page designs
  },
  // Index 2: Website Accessibility Audit
  {
    3: [0, 1], // Image alt text depends on color contrast and semantic HTML
    4: [1], // Tab order depends on semantic HTML
    5: [1, 4], // Hover states depend on semantic HTML and tab order
    6: [0], // Typography readability depends on color contrast
    7: [4], // Keyboard navigation depends on tab order
    8: [1, 4, 7], // ARIA roles depend on semantic HTML, tab order, keyboard nav
    9: [1, 8], // Form labels depend on semantic HTML and ARIA roles
    10: [1, 3, 7, 8, 9], // Screen reader testing depends on multiple previous tasks
  },
  // Index 3: Web Performance Optimization
  {
    1: [0], // Optimize load times depends on Lighthouse tests
    2: [0, 1], // Optimize images depends on tests and load times
    3: [0, 1], // Optimize videos depends on tests and load times
    4: [0, 1, 2, 3], // Minify depends on tests and media optimization
    5: [4], // Remove unused code depends on minification
    6: [4, 5], // Inline critical CSS/JS depends on minification and removal
    7: [6], // Defer third-party scripts depends on critical CSS/JS
    8: [1, 2, 3], // Loading indicators depend on optimization tasks
  },
  // Index 4: Cross-Browser Testing
  {
    4: [0, 1, 2, 3], // Mobile testing depends on all browser tests
  },
  // Index 5: Developer Experience & Documentation
  {
    1: [0], // Document tooling depends on architecture docs
    2: [0, 1], // Maintainer needs depend on both doc tasks
  },
  // Index 6: User Experience Enhancement
  {
    2: [0], // Loading indicators depend on type sizes
    3: [0], // Password toggle depends on type sizes
    4: [2, 3], // Button actions depend on loading and password toggle
    5: [4], // Feedback messages depend on button actions
    6: [4], // Browser navigation depends on button actions
    7: [4], // Mobile navigation depends on button actions
    8: [4, 5, 6, 7], // Form validation depends on multiple UX tasks
  },
  // Index 7: Design & Visual Consistency Review
  {
    1: [0], // Visual hierarchy depends on layout consistency
    2: [0, 1], // Image resolution depends on layout and hierarchy
    3: [0], // Color consistency depends on layout
    4: [0, 3], // Iconography depends on layout and color
    5: [0, 3, 4], // Border consistency depends on layout, color, and iconography
  },
  // Index 8: SEO Implementation
  {
    2: [0, 1], // Page titles depend on social images and favicon
    3: [2], // Meta descriptions depend on page titles
    4: [2, 3], // Header tags depend on titles and descriptions
    5: [0, 4], // Image alt text depends on social images and header structure
    6: [2, 3, 4], // Canonical tags depend on titles, descriptions, headers
    7: [4, 5, 6], // Sitemap depends on header structure, images, canonical
    8: [7], // Robots.txt depends on sitemap
    10: [7, 8], // Test links depends on sitemap and robots.txt
    11: [7, 10], // Redirects depend on sitemap and link testing
  },
  // Index 9: Copywriting & Content Review
  {
    1: [0], // Grammar proof depends on spell-check
    2: [1], // Optimize headlines depends on grammar proof
  },
  // Index 10: Calls to Action Optimization
  {
    1: [0], // Implement primary CTA depends on determining it
    3: [1], // Implement secondary CTAs depends on primary implementation
    5: [3], // Implement tertiary CTAs depends on secondary implementation
  },
  // Index 11: Marketing Analytics Setup
  {
    1: [0], // Verify tracking depends on installation
  },
  // Index 12: DevOps & Monitoring
  {
    1: [0], // Enable monitoring depends on backups
  },
  // Index 13: Security Implementation
  {
    1: [0], // Fix mixed-content depends on HTTPS enforcement
    2: [0, 1], // Protect input fields depends on HTTPS and mixed-content fix
    3: [2], // Secure password fields depends on input protection
  },
  // Index 14: Legal Compliance Setup
  {}, // Single task, no dependencies
  // Index 15: Website Launch
  {
    1: [0], // Draft launch plan depends on domain access
    2: [1], // Schedule launch depends on launch plan
    3: [2], // Execute launch depends on schedule
    4: [3], // Monitor depends on execution
  },
  // Index 16: Component Quality Assurance
  {
    2: [0, 1], // Mobile navigation depends on menu and browser navigation
    3: [0], // Form validation depends on menu links
    4: [3], // Form messages depend on validation
    5: [3], // Form tab order depends on validation
    6: [3, 4], // Form submissions depend on validation and messages
    7: [6], // Form buttons depend on submissions
    8: [3], // Form labels depend on validation
    9: [0], // Button actions depend on menu links
    10: [9], // Button states depend on button actions
    11: [2, 3], // Dropdown keyboard nav depends on mobile nav and validation
  },
  // Index 17: Project Handoff & Documentation
  {
    1: [0], // README depends on project docs
    2: [0, 1], // Handoff depends on both doc tasks
    3: [2], // Reflection depends on handoff
    4: [2, 3], // Portfolio shots depend on handoff and reflection
    5: [2, 3], // Case study depends on handoff and reflection
  },
]

/**
 * Generate consistent tasks for a project based on templates
 */
export function generateTasksForProject(project: Project): Task[] {
  // Ensure project has required properties
  if (!project || !project.workspace || !project.users || project.users.length === 0) {
    throw new Error('Invalid project provided to generateTasksForProject')
  }

  const templates = TASK_TEMPLATES_BY_PROJECT[project.name]

  if (!templates || templates.length === 0) {
    console.warn(`No task templates found for project: ${project.name}`)
    return []
  }

  const tasks: Task[] = []
  const creator = project.users[0]

  // First pass: Create all tasks
  templates.forEach((template) => {
    // Randomly assign to project users (80% chance)
    const assignee = faker.helpers.maybe(() => faker.helpers.arrayElement(project.users), {
      probability: 0.8,
    })

    // Generate due date if template specifies
    const dueDate = template.hasDueDate
      ? faker.date.between({
          from: faker.date.recent({ days: 30 }),
          to: faker.date.future({ years: 0.5 }),
        })
      : undefined

    const task: Task = {
      id: faker.string.uuid(),
      createdAt: faker.date.past({ years: 0.5 }),
      updatedAt: faker.date.recent({ days: 7 }),
      creator,
      type: template.type,
      name: template.name,
      description: template.description,
      status: template.status,
      priority: template.priority,
      horizon: template.horizon,
      dueDate,
      assignee,
      workspace: project.workspace,
      projects: [project],
      subtasks: [],
      messages: [],
      documents: [],
      dependencies: [],
      // Avoid duplicate users if creator and assignee are the same person
      users: assignee && assignee.id !== creator.id ? [creator, assignee] : [creator],
    }

    tasks.push(task)
  })

  // Second pass: Add dependencies based on project template index
  const projectTemplateIndex = PROJECT_TEMPLATES.findIndex((pt) => pt.name === project.name)
  const dependencyMap = TASK_DEPENDENCIES_BY_PROJECT_INDEX[projectTemplateIndex]

  if (dependencyMap) {
    Object.entries(dependencyMap).forEach(([taskIndexStr, dependencyIndices]) => {
      const taskIndex = parseInt(taskIndexStr, 10)
      const task = tasks[taskIndex]

      if (task) {
        // Map dependency indices to actual Task objects
        task.dependencies = dependencyIndices
          .map((depIndex) => tasks[depIndex])
          .filter((depTask) => depTask !== undefined)
      }
    })
  }

  return tasks
}
